// Sperren am Kontakt (ST-CAM-001 C1a/C1b; Michael E5, E2 b, E7 a; Vorgaben DESIGN
// 15./16.10.): lesen, eintragen und aufheben über /parties/{id}/communication-suppressions.
// Aufheben mit Pflichtgrund aus geschlossener Liste; Widerspruch und Rechtliche
// Sperre hebt nur der Datenschutz auf (E7 a), die Oberfläche bietet es dort nicht an.
//
// REINES MODELL, kein React, kein DOM.
//
// WÖRTER (Glossar, verbindlich): "Sperre" ist das Ding, "eintragen" das Verb;
// Kanal "E-Mail", "Telefon", "Post"; Geltung "Für die ganze Gruppe" bzw. "Nur für <Name>".
// Die Bedeutung trägt das Wort "gesperrt", nie die Farbe allein (WCAG 1.4.1).

import type { PlainMessage } from './access.ts';
import { readResource, sendCommand, type CommandMessages, type CommandOutcome, type SendLike } from './command-client.ts';
import { artWort, hausArtOf, type HausArt } from './haus-art.ts';

export type SuppressionChannel = 'email' | 'phone' | 'postal';
export type SuppressionReason = 'objection' | 'unsubscribe' | 'hard_bounce' | 'internal_block' | 'legal_hold';
export type SuppressionScope = 'group' | 'house';

export interface SuppressionView {
  readonly suppressionId: string;
  readonly channel: SuppressionChannel;
  readonly reasonCode: SuppressionReason;
  readonly scope: SuppressionScope;
  /** Gilt die Sperre einem einzelnen Weg, dessen Kennung; sonst dem ganzen Kanal. */
  readonly contactPointId?: string;
  /** Bei einer Haus-Sperre der Name des Hauses. */
  readonly houseName?: string;
  readonly validFrom: string;
  /** Die Version fürs Aufheben; ohne sie bietet die Zeile kein Aufheben an. */
  readonly lockVersion?: number;
}

export type HouseScope =
  | { readonly available: true; readonly operatingContextId: string; readonly displayName: string; readonly kind: HausArt | null }
  | { readonly available: false };

export interface SuppressionsData {
  readonly suppressions: readonly SuppressionView[];
  readonly additionalSuppressionCount: number;
  readonly houseScope: HouseScope;
}

export const CHANNEL_WORD: Readonly<Record<SuppressionChannel, string>> = Object.freeze({ email: 'E-Mail', phone: 'Telefon', postal: 'Post' });

export const REASONS: readonly { readonly code: SuppressionReason; readonly wort: string; readonly hinweis: string }[] = Object.freeze([
  { code: 'objection', wort: 'Widerspruch', hinweis: 'Die Person hat der Nutzung ihrer Daten für diesen Weg widersprochen.' },
  { code: 'unsubscribe', wort: 'Abmeldung', hinweis: 'Die Person hat sich von Zusendungen abgemeldet.' },
  { code: 'hard_bounce', wort: 'Nicht zustellbar', hinweis: 'Zustellung ist dauerhaft gescheitert, z. B. Adresse gibt es nicht.' },
  { code: 'internal_block', wort: 'Interne Sperre', hinweis: 'Intern entschieden, z. B. nach einer Beschwerde.' },
  { code: 'legal_hold', wort: 'Rechtliche Sperre', hinweis: 'Aus rechtlichen Gründen, z. B. ein laufendes Verfahren.' },
]);

export function reasonWord(code: SuppressionReason): string {
  return REASONS.find((grund) => grund.code === code)?.wort ?? code;
}

const CHANNELS = new Set<string>(['email', 'phone', 'postal']);
const REASON_CODES = new Set<string>(REASONS.map((grund) => grund.code));

function suppressionOf(raw: unknown): SuppressionView | undefined {
  const row = (raw ?? {}) as Record<string, unknown>;
  if (
    typeof row.suppressionId !== 'string' ||
    typeof row.channel !== 'string' ||
    !CHANNELS.has(row.channel) ||
    typeof row.reasonCode !== 'string' ||
    !REASON_CODES.has(row.reasonCode) ||
    (row.scope !== 'group' && row.scope !== 'house') ||
    typeof row.validFrom !== 'string'
  ) {
    return undefined;
  }
  return {
    suppressionId: row.suppressionId,
    channel: row.channel as SuppressionChannel,
    reasonCode: row.reasonCode as SuppressionReason,
    scope: row.scope,
    ...(typeof row.contactPointId === 'string' && row.contactPointId !== '' ? { contactPointId: row.contactPointId } : {}),
    ...(row.scope === 'house' && typeof row.operatingContextDisplayName === 'string' && row.operatingContextDisplayName !== ''
      ? { houseName: row.operatingContextDisplayName }
      : {}),
    validFrom: row.validFrom,
    ...(typeof row.lockVersion === 'number' && Number.isSafeInteger(row.lockVersion) ? { lockVersion: row.lockVersion } : {}),
  };
}

function houseScopeOf(raw: unknown): HouseScope {
  const row = (raw ?? {}) as Record<string, unknown>;
  // Fail-closed: ohne vollständige Angabe gibt es kein "Nur für <Haus>".
  return row.available === true && typeof row.operatingContextId === 'string' && typeof row.displayName === 'string' && row.displayName !== ''
    ? { available: true, operatingContextId: row.operatingContextId, displayName: row.displayName, kind: hausArtOf(row.kind) }
    : { available: false };
}

export type SuppressionsResult = { readonly outcome: 'loaded'; readonly data: SuppressionsData } | { readonly outcome: 'failed' };

/**
 * Jeder Ausgang außer einer gültigen Antwort ist "nicht geladen" -- NIE "keine
 * Sperre": ein Ladefehler hieße sonst fälschlich "frei" (NFR-UX-005).
 */
export async function fetchSuppressions(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly send?: SendLike;
}): Promise<SuppressionsResult> {
  const result = await readResource({
    url: `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/communication-suppressions`,
    accessToken: options.accessToken,
    ...(options.send === undefined ? {} : { send: options.send }),
  });
  if (result.outcome !== 'loaded' || !Array.isArray(result.body.suppressions)) return { outcome: 'failed' };
  // Fail-closed: eine unlesbare Zeile kann eine Sperre sein; verworfen hiesse sie "frei".
  const suppressions = result.body.suppressions.map((row: unknown) => suppressionOf(row));
  if (suppressions.some((sperre) => sperre === undefined)) return { outcome: 'failed' };
  const zusatz = result.body.additionalSuppressionCount;
  return {
    outcome: 'loaded',
    data: {
      suppressions: suppressions as SuppressionView[],
      additionalSuppressionCount: typeof zusatz === 'number' && Number.isSafeInteger(zusatz) && zusatz > 0 ? zusatz : 0,
      houseScope: houseScopeOf(result.body.houseScope),
    },
  };
}

// --- Sätze -------------------------------------------------------------------

/** "Für E-Mail gesperrt – Widerspruch" bzw. "Für <Wert> gesperrt – …" bei einem einzelnen Weg. */
export function suppressionLine(sperre: SuppressionView, wertDesWegs: (contactPointId: string) => string | undefined): string {
  const grund = reasonWord(sperre.reasonCode);
  if (sperre.contactPointId === undefined) return `Für ${CHANNEL_WORD[sperre.channel]} gesperrt – ${grund}`;
  const wert = wertDesWegs(sperre.contactPointId);
  if (wert !== undefined) return `Für ${wert} gesperrt – ${grund}`;
  return `Für eine frühere ${sperre.channel === 'phone' ? 'Telefonnummer' : 'E-Mail-Adresse'} gesperrt – ${grund}`;
}

/**
 * "Für die ganze Gruppe" bzw. "Nur für <Name>" (A2). Ohne Namen ehrlich nach Art; eine
 * einzelne Sperre traegt keine Art (#732), dann heisst es "Nur für einen Verlag".
 */
export function scopeWords(scope: SuppressionScope, houseName: string | undefined): string {
  if (scope === 'group') return 'Für die ganze Gruppe';
  return houseName === undefined ? `Nur für ${artWort(null, 'einen')}` : `Nur für ${houseName}`;
}

export function suppressionMeta(sperre: SuppressionView, formatDate: (iso: string) => string): string {
  return `${scopeWords(sperre.scope, sperre.houseName)} · seit ${formatDate(sperre.validFrom)}`;
}

export function additionalSuppressions(anzahl: number): string | undefined {
  if (anzahl <= 0) return undefined;
  return anzahl === 1 ? 'und 1 weitere Sperre' : `und ${anzahl} weitere Sperren`;
}

export const NO_SUPPRESSION_TEXT = 'Keine Sperre eingetragen.';
/** An einer Zeile ohne bekannte Sperre, wenn die Sperren nicht vollständig geladen sind (fail-closed). */
export const INCOMPLETE_LINE_HINT = 'Sperren unvollständig – vor Anruf oder Schreiben prüfen';
/** Im Dialog, wenn der Kontakt mehr Wege hat, als die Auswahl zeigt. */
export function truncatedWaysNote(channel: 'email' | 'phone'): string {
  return channel === 'email'
    ? 'Hier stehen nicht alle Adressen. Fehlt die gesuchte, wähle „Alle E-Mail-Adressen“.'
    : 'Hier stehen nicht alle Nummern. Fehlt die gesuchte, wähle „Alle Telefonnummern“.';
}
export const SUPPRESSIONS_LOAD_FAILED: PlainMessage = Object.freeze({
  headline: 'Die Sperren konnten nicht geladen werden.',
  explanation: 'Das sagt nichts darüber, ob der Kontakt gesperrt ist.',
  nextStep: 'Lade die Sperren erneut, bevor du anrufst oder schreibst.',
  retryable: true,
});
export const DIALOG_NOTE = 'Prüfe Weg und Grund, bevor du einträgst. Eine Sperre lässt sich nur mit Grund aufheben, Widerspruch und Rechtliche Sperre nur durch den Datenschutz.';

export function addedAnnouncement(channel: SuppressionChannel, wert: string | undefined, reason: SuppressionReason, scope: SuppressionScope, houseScope: HouseScope): string {
  const ziel = wert ?? CHANNEL_WORD[channel];
  const geltung = scopeWords(scope, houseScope.available ? houseScope.displayName : undefined);
  // Im Satz klein: "…, für die ganze Gruppe." bzw. "…, nur für Verlag Nord."
  return `Sperre eingetragen: Für ${ziel} gesperrt – ${reasonWord(reason)}, ${geltung.charAt(0).toLowerCase()}${geltung.slice(1)}.`;
}

/** Die Sperren, die einen Kontaktweg treffen: auf ihn selbst oder auf seinen ganzen Kanal. */
export function suppressionsFor(sperren: readonly SuppressionView[], channel: SuppressionChannel, contactPointId?: string): readonly SuppressionView[] {
  return sperren.filter((sperre) => sperre.channel === channel && (sperre.contactPointId === undefined || sperre.contactPointId === contactPointId));
}

/** Der Hinweis an der Zeile: "Gesperrt – <Grund>", bei mehreren dazu "+ N weitere". */
export function lineHint(treffer: readonly SuppressionView[]): { readonly text: string; readonly weitere: number } | undefined {
  const erste = treffer[0];
  if (erste === undefined) return undefined;
  return { text: `Gesperrt – ${reasonWord(erste.reasonCode)}`, weitere: treffer.length - 1 };
}

export const formatSuppressionDate = (iso: string): string =>
  new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' }).format(new Date(iso));

// --- Eintragen ---------------------------------------------------------------

const NOT_WRITABLE: PlainMessage = Object.freeze({
  headline: 'An diesem Kontakt lässt sich keine Sperre mehr eintragen.',
  explanation: 'Der Kontakt ist archiviert, anonymisiert oder mit einem anderen zusammengeführt.',
  nextStep: 'Geh zurück zum Kontakt und prüfe seinen Stand.',
  retryable: false,
});

export const DUPLICATE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Sperre gibt es schon.',
  explanation: 'Für diesen Weg ist mit diesem Grund bereits eine Sperre eingetragen. Es wurde nichts geändert.',
  nextStep: 'Du musst nichts weiter tun.',
  retryable: false,
});

export const CONTEXT_AMBIGUOUS_MESSAGE: PlainMessage = Object.freeze({
  headline: '„Nur für einen Verlag“ geht hier nicht.',
  explanation: 'Der Kontakt gehört zu mehreren Gesellschaften der Gruppe, und LIAISON kann nicht entscheiden, welche gemeint ist. Es wurde nichts eingetragen.',
  nextStep: 'Wähle „Für die ganze Gruppe“.',
  retryable: false,
});

export const CONTACT_POINT_GONE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diesen Weg gibt es nicht mehr.',
  explanation: 'Er wurde inzwischen geändert oder beendet. Es wurde nichts eingetragen.',
  nextStep: 'Die Auswahl ist neu geladen. Wähle den Weg erneut oder „Alle“.',
  retryable: false,
});

export const UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob die Sperre eingetragen ist, ist offen.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: 'Lade die Sperren neu und prüfe, ob sie dasteht.',
  retryable: true,
});

/** Ein 409 ohne eigenen Satz: LIAISON hat geantwortet und abgelehnt, offen ist dabei nichts. */
export const CONFLICT_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Sperre wurde nicht eingetragen.',
  explanation: 'Der Stand hat sich inzwischen geändert.',
  nextStep: 'Die Sperren sind neu geladen. Versuche es erneut.',
  retryable: false,
});

export const SUPPRESSION_MESSAGES: CommandMessages = Object.freeze({
  notFound: NOT_WRITABLE,
  conflictFallback: CONFLICT_MESSAGE,
  conflicts: Object.freeze({
    communication_suppression_duplicate: DUPLICATE_MESSAGE,
    communication_suppression_context_ambiguous: CONTEXT_AMBIGUOUS_MESSAGE,
    contact_point_not_found: CONTACT_POINT_GONE_MESSAGE,
    party_not_writable: NOT_WRITABLE,
    client_request_reused: Object.freeze({
      headline: 'Dein erster Versuch ist mit anderen Angaben angekommen.',
      explanation: 'Deine jetzige Auswahl wurde nicht eingetragen. Sie steht noch im Dialog.',
      nextStep: 'Lade die Sperren neu und prüfe, ob die Sperre schon dasteht.',
      retryable: false,
    }),
    client_request_superseded: Object.freeze({
      headline: 'Diese Sperre ist schon angekommen.',
      explanation: 'Dein erster Versuch wurde eingetragen; ein weiterer ändert nichts.',
      nextStep: 'Lade die Sperren neu und sieh nach, was jetzt gilt.',
      retryable: false,
    }),
  }),
});

export type AddSuppressionResult = CommandOutcome<{ readonly eingetragen: true }>;

export function addSuppression(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly input: { readonly channel: SuppressionChannel; readonly reasonCode: SuppressionReason; readonly scope: SuppressionScope; readonly contactPointUsageId?: string };
  readonly clientRequestId: string;
  readonly send?: SendLike;
}): Promise<AddSuppressionResult> {
  return sendCommand({
    url: `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/communication-suppressions`,
    accessToken: options.accessToken,
    body: {
      channel: options.input.channel,
      reasonCode: options.input.reasonCode,
      scope: options.input.scope,
      ...(options.input.contactPointUsageId === undefined ? {} : { contactPointUsageId: options.input.contactPointUsageId }),
      clientRequestId: options.clientRequestId,
    },
    messages: SUPPRESSION_MESSAGES,
    applied: () => ({ eingetragen: true as const }),
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

// --- Aufheben (C1b; Vorgabe DESIGN A1-A7) ------------------------------------

export type LiftReason = 'new_consent' | 'contact_corrected' | 'reason_ended' | 'entered_in_error';

export const LIFT_REASONS: Readonly<Record<LiftReason, { readonly wort: string; readonly hinweis: string }>> = Object.freeze({
  new_consent: { wort: 'Neue Einwilligung', hinweis: 'Die Person hat wieder zugestimmt, z. B. sich neu angemeldet.' },
  contact_corrected: { wort: 'Angabe korrigiert', hinweis: 'Adresse oder Nummer ist berichtigt und wieder erreichbar.' },
  reason_ended: { wort: 'Grund entfallen', hinweis: 'Der Anlass besteht nicht mehr, z. B. Verfahren beendet.' },
  entered_in_error: { wort: 'Versehentlich eingetragen', hinweis: 'Die Sperre war ein Irrtum.' },
});

/**
 * Welche Aufhebegründe je Sperrgrund passen (A1); der Server prüft dieselbe
 * Zuordnung (422 lift_reason_not_applicable). Widerspruch und Rechtliche Sperre
 * fehlen: sie hebt nur der Datenschutz auf (E7 a, 409 not_liftable).
 */
const PASSENDE: Readonly<Partial<Record<SuppressionReason, readonly LiftReason[]>>> = Object.freeze({
  unsubscribe: ['new_consent', 'entered_in_error'],
  hard_bounce: ['contact_corrected', 'entered_in_error'],
  internal_block: ['reason_ended', 'entered_in_error'],
});

export function liftReasonsFor(reason: SuppressionReason): readonly LiftReason[] {
  return PASSENDE[reason] ?? [];
}

export function isLiftable(reason: SuppressionReason): boolean {
  return liftReasonsFor(reason).length > 0;
}

/** Ob an dieser Zeile "Aufheben …" angeboten wird -- dieselbe Bedingung im Abschnitt Sperren und beim Korrigieren eines Kontaktwegs. */
export function liftOffered(sperre: SuppressionView): boolean {
  return isLiftable(sperre.reasonCode) && sperre.lockVersion !== undefined;
}

export const ONLY_PRIVACY_LIFTS = 'Aufheben nur durch den Datenschutz.';
export const LIFT_NOTE = 'Danach zählt die Sperre nicht mehr für Export und Versand. Die Aufhebung steht mit Grund am Kontakt.';
export const LIFT_REASON_REQUIRED = 'Wähle einen Grund.';

/** Der zugängliche Name des Knopfs an der Zeile: "Sperre aufheben: <Zeile>". */
export function liftButtonName(line: string): string {
  return `Sperre aufheben: ${line}`;
}

/** "Für E-Mail – Abmeldung" bzw. "Für <Wert> – <Grund>" (A4/A5). */
export function liftedLine(sperre: SuppressionView, wertDesWegs: (contactPointId: string) => string | undefined): string {
  return suppressionLine(sperre, wertDesWegs).replace(' gesperrt – ', ' – ');
}

export function liftedAnnouncement(line: string, reason: LiftReason): string {
  return `Sperre aufgehoben: ${line}. Grund: ${LIFT_REASONS[reason].wort}.`;
}

export interface EndedSuppressionView extends SuppressionView {
  readonly endedAt: string;
  readonly endReasonCode: LiftReason;
  /** Von der lesenden Person aufgehoben; sonst von einer Kollegin oder einem Kollegen (Michael R3: kein Name). */
  readonly endedByMe: boolean;
}

const LIFT_REASON_CODES = new Set<string>(Object.keys(LIFT_REASONS));

function endedOf(raw: unknown): EndedSuppressionView | undefined {
  const basis = suppressionOf(raw);
  const row = (raw ?? {}) as Record<string, unknown>;
  if (
    basis === undefined ||
    typeof row.endedAt !== 'string' ||
    typeof row.endReasonCode !== 'string' ||
    !LIFT_REASON_CODES.has(row.endReasonCode) ||
    typeof row.endedByMe !== 'boolean'
  ) {
    return undefined;
  }
  return { ...basis, endedAt: row.endedAt, endReasonCode: row.endReasonCode as LiftReason, endedByMe: row.endedByMe };
}

export type EndedSuppressionsResult =
  | { readonly outcome: 'loaded'; readonly suppressions: readonly EndedSuppressionView[]; readonly additionalSuppressionCount: number }
  | { readonly outcome: 'failed' };

/**
 * Die aufgehobenen Sperren (GET ?status=ended), neueste zuerst. Eine unlesbare
 * Zeile macht den Abruf "nicht geladen": eine verworfene Zeile oder ein geratenes
 * "von einer Kollegin" waeren eine falsche Auskunft über den Verlauf.
 */
export async function fetchEndedSuppressions(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly send?: SendLike;
}): Promise<EndedSuppressionsResult> {
  const result = await readResource({
    url: `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/communication-suppressions?status=ended`,
    accessToken: options.accessToken,
    ...(options.send === undefined ? {} : { send: options.send }),
  });
  if (result.outcome !== 'loaded' || !Array.isArray(result.body.suppressions)) return { outcome: 'failed' };
  const zeilen = result.body.suppressions.map((row: unknown) => endedOf(row));
  if (zeilen.some((zeile) => zeile === undefined)) return { outcome: 'failed' };
  const zusatz = result.body.additionalSuppressionCount;
  return { outcome: 'loaded', suppressions: zeilen as EndedSuppressionView[], additionalSuppressionCount: typeof zusatz === 'number' && Number.isSafeInteger(zusatz) && zusatz > 0 ? zusatz : 0 };
}

/** "aufgehoben am 16.10.2026 von dir · Neue Einwilligung" (A5); die Wörter wie im Verlauf. */
export function endedMeta(sperre: EndedSuppressionView, formatDate: (iso: string) => string, wer: (byMe: boolean) => string): string {
  return `aufgehoben am ${formatDate(sperre.endedAt)} ${wer(sperre.endedByMe)} · ${LIFT_REASONS[sperre.endReasonCode].wort}`;
}

export function endedHeading(anzahl: number, weitere: number): string {
  return `Aufgehobene Sperren (${anzahl + weitere})`;
}

/** Schließt den Dialog; der Abschnitt lädt neu und zeigt den Stand. */
export const LIFT_OUTDATED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Sperre wurde inzwischen geändert.',
  explanation: 'Vielleicht hat sie schon jemand aufgehoben.',
  nextStep: 'Der Abschnitt zeigt jetzt den aktuellen Stand.',
  retryable: false,
});

export const LIFT_NOT_LIFTABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Sperre hebt nur der Datenschutz auf.',
  explanation: 'Sie bleibt bestehen.',
  nextStep: '',
  retryable: false,
});

export const LIFT_REASON_NOT_APPLICABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieser Grund passt nicht zu dieser Sperre.',
  explanation: '',
  nextStep: 'Wähle einen der angebotenen Gründe.',
  retryable: false,
});

/** Nennt die Art des eigenen Hauses (A2); ohne eindeutiges Haus heisst es "deines Verlags". */
export function liftForbiddenMessage(art: HausArt | null | undefined): PlainMessage {
  return Object.freeze({
    headline: 'Du darfst an diesem Kontakt nichts pflegen.',
    explanation: 'Die Sperre bleibt bestehen.',
    nextStep: `Wende dich an die Pflege ${artWort(art, 'deines')}.`,
    retryable: false,
  });
}

export const LIFT_UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob die Sperre aufgehoben wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: '„Sperren erneut laden“ zeigt den Stand.',
  retryable: true,
});

/** Der Kontakt wurde nach dem Öffnen des Dialogs archiviert, anonymisiert oder zusammengeführt. */
export const LIFT_NOT_WRITABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'An diesem Kontakt lässt sich die Sperre nicht mehr aufheben.',
  explanation: 'Der Kontakt ist archiviert, anonymisiert oder mit einem anderen zusammengeführt.',
  nextStep: 'Geh zurück zum Kontakt und prüfe seinen Stand.',
  retryable: false,
});

export function liftMessages(art: HausArt | null | undefined): CommandMessages {
  return Object.freeze({
    notFound: LIFT_OUTDATED_MESSAGE,
    conflictFallback: LIFT_OUTDATED_MESSAGE,
    forbidden: liftForbiddenMessage(art),
    conflicts: Object.freeze({
      communication_suppression_outdated: LIFT_OUTDATED_MESSAGE,
      communication_suppression_already_ended: LIFT_OUTDATED_MESSAGE,
      communication_suppression_not_liftable: LIFT_NOT_LIFTABLE_MESSAGE,
      party_not_writable: LIFT_NOT_WRITABLE_MESSAGE,
    }),
    unprocessable: Object.freeze({
      lift_reason_not_applicable: LIFT_REASON_NOT_APPLICABLE_MESSAGE,
    }),
  });
}

/** Nach diesen Antworten schließt der Dialog, und der Abschnitt lädt neu (A6). */
export const LIFT_CLOSES_DIALOG: ReadonlySet<string> = new Set([
  'communication_suppression_outdated',
  'communication_suppression_already_ended',
  'communication_suppression_not_liftable',
  'communication_suppression_not_found',
]);

export type EndSuppressionResult = CommandOutcome<{ readonly aufgehoben: true }>;

/**
 * Hebt eine Sperre auf. Ohne Absendekennung (der Weg nimmt keine): ein zweiter
 * Versuch nach einem Abbruch trifft eine schon aufgehobene Sperre und bekommt 409
 * already_ended -- "inzwischen geändert", nichts doppelt.
 */
export function endSuppression(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly suppressionId: string;
  readonly endReasonCode: LiftReason;
  readonly lockVersion: number;
  /** Die Art des eigenen Hauses fuer den Satz ohne Recht. */
  readonly art?: HausArt | null;
  readonly send?: SendLike;
}): Promise<EndSuppressionResult> {
  return sendCommand({
    url: `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/communication-suppressions/${encodeURIComponent(options.suppressionId)}/end`,
    accessToken: options.accessToken,
    body: { endReasonCode: options.endReasonCode, lockVersion: options.lockVersion },
    messages: liftMessages(options.art),
    applied: () => ({ aufgehoben: true as const }),
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}
